import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  Theme,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import { Alert } from "@mui/material";
import DirtyFormSpy from "components/DirtyFormSpy";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import SelectProfession from "components/SelectProfession";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { pick } from "lodash";
import React, { useMemo, useState } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  SkuLearningObjective,
  SkuLearningObjectiveRequest,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  paper: {
    marginTop: "10px",
  },
}));

type SkuLearningObjectiveFormProps = {
  onClose: () => void;
  skuLearningObjective?: SkuLearningObjective | undefined;
  submitLabel: string;
};

const { Form } = withTypes<SkuLearningObjectiveRequest>();

const SkuLearningObjectiveForm = ({
  sku,
  skuLearningObjective,
  onClose,
  onClean,
  onDirty,
  submitLabel,
  onSkuUpdated,
}: SkuLearningObjectiveFormProps & SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const init = useMemo(
    () => ({
      description: skuLearningObjective?.learningObjective?.description!,
      ...pick(skuLearningObjective ?? {}, ["id", "professionId"]),
    }),
    [skuLearningObjective]
  );

  const form = useMemo(() => {
    const onSubmit = async (values: SkuLearningObjectiveRequest) => {
      try {
        const response = await registerPromise(
          skuService.saveSkuLearningObjective(
            sku.id!,
            SkuLearningObjectiveRequest.fromJS(values)
          )
        );

        queryClient.invalidateQueries(["skuLearningObjectives", sku.id!]);

        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "skuLearningObjectiveCreated" }))
        );

        onSkuUpdated && onSkuUpdated(sku.id!);
        onClose();

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DirtyFormSpy onDirty={onDirty} onClean={onClean} />
            <DialogTitle id="form-dialog-title">
              {t({ id: "skuLearningObjectiveTitle" })}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={6}>
                <Field name="professionId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <Tooltip
                        title={t({
                          id: "LearningObjectiveEmptyProfessionMessage",
                        })}
                      >
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProfession
                            label={t({
                              id: "professionLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Paper elevation={3} square sx={styles.paper}>
                    <Box
                      p={1}
                      display="flex"
                      alignItems="center"
                      data-cy="learningObjectivePlaceHolderText"
                    >
                      <Field name="description" required>
                        {({ input, meta }) => (
                          <FieldRichText
                            input={input}
                            meta={meta}
                            label={t({ id: "learningObjectivePlaceholder" })}
                          />
                        )}
                      </Field>
                    </Box>
                  </Paper>
                </Grid>
                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {submitting && <CircularProgress size={16} />}
              <Button
                disabled={submitting}
                onClick={onClose}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="submitButton"
              >
                {submitLabel}
              </Button>
            </DialogActions>
            <SnackbarMessage
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
            />
          </form>
        )}
      />
    );
  }, [
    styles.feedback,
    styles.paper,
    init,
    onClean,
    onClose,
    onDirty,
    onSkuUpdated,
    queryClient,
    registerPromise,
    sku.id,
    skuService,
    snackbarMessage,
    submitLabel,
    t,
  ]);

  return form;
};
export default SkuLearningObjectiveForm;
